"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Star, Quote, ShieldCheck } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  name: "ALEXANDER V.",
  role: "Executive Director",
  company: "Global Finance Group",
  protocol: "Executive Delegation",
  rating: 5,
  quote: "BEMAX LIMO CHAUFFEURED SERVICE provided an impeccable experience for our corporate delegation. Truly the gold standard in luxury transport."
}, {
  name: "SARAH JENKINS",
  role: "Managing Partner",
  company: "Jenkins & Associates",
  protocol: "Airport Transfer",
  rating: 5,
  quote: "Their attention to detail and commitment to discretion made our airport transfer feel like a seamless extension of our private office."
}, {
  name: "JULIAN THORNE",
  role: "Founder",
  company: "Thorne Capital",
  protocol: "Financial Roadshow",
  rating: 5,
  quote: "Every journey with BEMAX is defined by punctuality and refinement. They have mastered the art of the chauffeured experience."
}, {
  name: "MARCUS WELLINGTON",
  role: "CEO",
  company: "Wellington Holdings",
  protocol: "VIP Private Transit",
  rating: 5,
  quote: "The vehicle was pristine, the chauffeur was professional, and the entire process was effortless. Simply the best in the city."
}, {
  name: "ELENA VOSS",
  role: "Head of Operations",
  company: "Voss Foundation",
  protocol: "Diplomatic Service",
  rating: 5,
  quote: "Discretion, precision, and absolute comfort. BEMAX LIMO CHAUFFEURED SERVICE understands the requirements of high-profile travel."
}, {
  name: "DAVID STERLING",
  role: "Principal",
  company: "Sterling Family Office",
  protocol: "Private Aviation Transfer",
  rating: 5,
  quote: "Seamless coordination on the tarmac with zero room for error. The standard of confidentiality and punctuality is unmatched."
}];
export default function Testimonials() {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  return <section data-section-id="3731" id="client-perspectives" ref={sectionRef} className="relative bg-black text-white py-24 md:py-32 lg:py-36 px-6 md:px-10 lg:px-14 overflow-hidden border-t border-b border-white/10">
      {/* Background Ambience / Subtle Silver Grid Glow */}
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.04),transparent_60%)]" />

      <div className="max-w-7xl mx-auto relative">
        {/* Editorial Section Header */}
        <div className={`mb-16 md:mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 pb-8 border-b border-white/10">
            <div className="space-y-4 max-w-2xl">
              <div className="flex items-center gap-4">
                <span className="font-mono text-xs tracking-[0.25em] text-white/50 uppercase">
                  § 02 // REVIEWS
                </span>
                <span className="h-px w-10 bg-white/20" />
                <span className="font-mono text-xs tracking-[0.25em] text-white/80 uppercase">
                  CLIENT PERSPECTIVES
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-light tracking-tight text-white leading-[1.05]">
                The standard of arrival.
              </h2>
            </div>
            <p className="max-w-md text-sm sm:text-base text-white/60 font-light leading-relaxed font-sans">
              Trusted by corporate leaders, family offices, and private delegations who demand seamless punctuality, absolute discretion, and uncompromising comfort.
            </p>
          </div>
        </div>

        {/* Carousel Component */}
        <div className={`transition-all duration-1000 delay-150 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Carousel setApi={setApi} opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((item, i) => <CarouselItem key={item.name} data-index={i} className="pl-4 md:pl-6 basis-full md:basis-1/2 lg:basis-1/3">
                  <div className="group relative flex flex-col justify-between h-full min-h-[380px] p-8 md:p-9 bg-[#050505] border border-white/10 hover:border-white/30 rounded-lg transition-all duration-500 overflow-hidden shadow-sm">
                    {/* Top Silver Accent Line */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-500" />

                    {/* Card Header: Rating & Verified Protocol Badge */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 text-[#E6E6E6]">
                          {[...Array(item.rating)].map((_, starIndex) => <Star key={starIndex} className="size-3.5 fill-white/90 text-white/90" />)}
                        </div>
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md font-mono text-[10px] tracking-wider text-white/70 uppercase">
                          <ShieldCheck className="size-3 text-white/80" />
                          <span>Verified Journey</span>
                        </div>
                      </div>

                      {/* Quote Content */}
                      <div className="relative pt-2">
                        <Quote className="size-6 text-white/15 mb-3" />
                        <p className="font-serif italic font-light text-white/90 text-base md:text-lg leading-relaxed">
                          &ldquo;{item.quote}&rdquo;
                        </p>
                      </div>
                    </div>

                    {/* Card Footer: Client Info & Protocol */}
                    <div className="pt-8 mt-6 border-t border-white/10 space-y-3">
                      <div>
                        <div className="font-sans font-medium text-xs tracking-[0.2em] text-white uppercase">
                          {item.name}
                        </div>
                        <div className="text-xs text-white/50 font-light mt-0.5">
                          {item.role} · {item.company}
                        </div>
                      </div>
                      <div className="font-mono text-[10px] tracking-[0.2em] text-white/40 uppercase">
                        PROTOCOL // {item.protocol}
                      </div>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {/* Bottom Navigation & Index Counter */}
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-white/10">
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm tracking-widest text-white">
                {String(current || 1).padStart(2, "0")}
              </span>
              <span className="text-xs text-white/30">/</span>
              <span className="font-mono text-xs tracking-widest text-white/40">
                {String(count || testimonials.length).padStart(2, "0")}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => api?.scrollPrev()} aria-label="Previous testimonial" className="size-10 rounded-lg border-white/20 bg-black/40 text-white hover:bg-white hover:text-black hover:border-white transition-all duration-300">
                <ArrowLeft className="size-4" />
              </Button>
              <Button variant="outline" size="icon" onClick={() => api?.scrollNext()} aria-label="Next testimonial" className="size-10 rounded-lg border-white/20 bg-black/40 text-white hover:bg-white hover:text-black hover:border-white transition-all duration-300">
                <ArrowRight className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Operational Trust Bar */}
        <div className={`mt-20 pt-10 border-t border-white/10 text-center transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.25em] text-white/50 uppercase">500,000+ MILES MANAGED<span style={{
            caretColor: 'oklab(1 0 0 / 0.2)',
            color: 'oklab(1 0 0 / 0.2)'
          }}></span>% ON-TIME DISPATCH<span className="mx-3 text-white/20">·</span>STRICT DISCRETION PROTOCOLS</p>
        </div>
      </div>
    </section>;
}